"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";
import ShareButtons from "@/components/common/ShareButtons";
import Button from "@/components/library/Button";
import type { JobListing } from "@/utils/jobsPublic";
import { jobTypeLabel } from "@/utils/jobsPublic";

type JobCardProps = {
  job: JobListing;
  shareUrl: string;
  detailsHref: string;
  applyHref: string;
};

const TYPE_BADGE_CLASS: Record<JobListing["type"], string> = {
  engineering: "bg-gail-primary-light text-gail-primary",
  gtm: "bg-gail-primary-light text-gail-primary",
  cx: "bg-gail-primary-light text-gail-primary",
};

export default function JobCard({
  job,
  shareUrl,
  detailsHref,
  applyHref,
}: Readonly<JobCardProps>) {
  const { t } = useTranslation();

  return (
    <article className="flex flex-col bg-white shadow-sm hover:shadow-md focus-within:shadow-md p-6 border rounded-2xl h-full transition-shadow border-black/10">
      <Link
        href={detailsHref}
        className="flex flex-col flex-1 text-left cursor-pointer"
        aria-label={t("careers.viewDetails", { jobTitle: job.title })}
      >
        <span
          className={`inline-flex px-3 py-1 rounded-full w-fit font-monument font-bold text-xs tracking-wide uppercase ${TYPE_BADGE_CLASS[job.type]}`}
        >
          {job.departmentLabel ?? jobTypeLabel(job.type)}
        </span>

        <h3 className="mt-4 font-monument font-medium text-foreground text-2xl leading-tight">
          {job.title}
        </h3>

        <p className="flex-1 mt-3 text-sm line-clamp-4 font-monument leading-normal text-gail-gray">
          {job.summary}
        </p>

        {job.location ? (
          <p className="flex items-center gap-2 mt-4 text-sm font-monument text-gail-gray">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width={16}
              height={16}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {job.location}
          </p>
        ) : null}

        {job.compensation ? (
          <p className="mt-2 text-sm font-monument font-medium text-black">
            {job.compensation}
          </p>
        ) : null}
      </Link>

      <div className="flex justify-between items-center mt-6 pt-4 border-t gap-3 border-black/10">
        <div className="flex items-center gap-2">
          <span className="text-sm font-monument text-gail-gray">
            {t("careers.share")}
          </span>
          <ShareButtons
            url={shareUrl}
            title={job.title}
            size={24}
            variant="dark"
            utmContent={job.id}
          />
        </div>
        <Button
          type="primary"
          {...(job.applyEnabled === false
            ? { htmlType: "button" as const, disabled: true }
            : { href: applyHref })}
          className="inline-flex! justify-center items-center px-5! py-0! h-10"
          textClassName="text-sm leading-none font-monument"
        >
          {t("careers.apply")}
        </Button>
      </div>
    </article>
  );
}
